import React, { useState, useEffect, useRef, useCallback } from "react";

import { CustomConversationBubble as ConversationBubble, CustomTextarea } from "_components";
import { useInvoices } from "_contexts";
import { isNonEmptyArray, isNonEmptyObject, formatToCapitalize, defineImgSrcToDownload as defineSrc, isEmpty, isNonEmpty } from "_helpers";
import { useFolderDetailGetMessages, useFolderDetailGetMessageAttachment, useFolderDetailSendMessage, useFolderDetailSendNextFile, useTextarea } from "_hooks";

import { 
    Button, 
    Card, 
    Col, 
    Empty, 
    Image, 
    message, 
    Modal, 
    Row, 
    Tag, 
    Tooltip,
    Typography
} from "antd";
import parse from 'html-react-parser';

import { ReactComponent as CloseModal } from "_assets/close-window.svg";

import styles from "_styles/Components/_Modal.module.css";

import dayjs from "dayjs";

export const CustomModal = ({ payload, summary, modalHandler }) => {
    const [ messages, setMessages ] = useState({});

    const [ quizStep, setQuizStep ] = useState(0);

    const [ quizNextFileMessageVisible, setQuizNextFileMessageVisible ] = useState(false);
    const [ quizNextFileMessageRequired, setQuizNextFileMessageRequired ] = useState(true);

    const [ rowChatWidth, setRowChatWidth ] = useState(0);

    const { modalIsOpen, setModalIsOpen } = modalHandler;

    const { fetchMessages, fetchQuiz, doReload } = useFolderDetailGetMessages( modalIsOpen, payload );
    const { setBinId } = useFolderDetailGetMessageAttachment();
    const { textareaValuesHandler, textareaResetValues, setSubmit, successMessage, errorMessage } = useFolderDetailSendMessage( payload );
    const { answers, setAnswers, postNextFile } = useFolderDetailSendNextFile( payload );

    const { setLitigMessages } = useInvoices();

    const { values } = textareaValuesHandler;

    const messagesBoxRef = useRef();
    const quizNextFileTextareaRef = useRef();
    const textareaRef = useRef();
    const rowChatRef = useRef();

    const quizNextFileTextareaHeight = useTextarea(quizNextFileTextareaRef.current, answers?.nextFileComment);
    const textareaHeight = useTextarea(textareaRef.current, values.text);

    const { Text } = Typography;

    const [ messageApi, contextHolder ] = message.useMessage();

    const success = useCallback(( message ) => {
        messageApi.success(message);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    } , [])

    const error = useCallback(( message, duration = 1.5 ) => {
        messageApi.error(message, duration);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    } , [])

    useEffect(() => {
        if( successMessage || errorMessage ){
            if( successMessage ){
                success(successMessage);
            } else {
                error(errorMessage);
            }
            textareaResetValues();
            setSubmit(false);
            doReload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successMessage, errorMessage])

    useEffect(() => {
        messagesOrdering();
        setLitigMessages(fetchMessages);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchMessages])

    useEffect(() => {
        if( modalIsOpen && messagesBoxRef.current ){
            const messagesBox = messagesBoxRef.current;

            messagesBox.scrollTo(0, messagesBox.scrollHeight);
        }
        if( !modalIsOpen ){
            textareaResetValues();
            setQuizStep(0);
            setQuizNextFileMessageVisible(false);
            setQuizNextFileMessageRequired(true);
            setAnswers({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalIsOpen, messagesBoxRef.current])

    useEffect(() => {
        if( rowChatRef?.current && rowChatRef.current.clientWidth ){
            setRowChatWidth(rowChatRef.current.clientWidth);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowChatRef?.current, messages])

    const messagesOrdering = useCallback(() => {
        if( isNonEmptyArray(fetchMessages) ){
            const reduced = fetchMessages.reduce((acc, val) => {
                const { msg_direction, msg_date, msg_txt, ...rest } = val;
    
                const time = dayjs(msg_date).format('HH:mm');
                const fullDay = dayjs(msg_date).format('DD/MM/YYYY');
                const day = formatToCapitalize(dayjs(msg_date).format('dddd'));
    
                const difference = dayjs().diff(dayjs(msg_date).startOf('day'), 'day');
                let key;
                if( difference === 0 ){
                    key = 'Aujourd\'hui';
                } else if( difference === 1 ){
                    key = 'Hier';
                } else if( difference === 2 ){
                    key = 'Avant-Hier';
                } else if( difference > 2 && difference < 7 ){
                    key = day;
                } else {
                    key = fullDay;
                }

                if( !acc[key] ){
                    acc[key] = [];
                }
    
                acc[key].push({
                    msg_direction,
                    msg_time: time,
                    msg_txt,
                    ...rest
                })

                return acc;
            }, {});

            const reeorder = Object.fromEntries(Object.entries(reduced)
                .sort((a,b) => {
                    const aa = a[0];
                    const bb = b[0];

                    const daysOrder = [
                        'Avant-Hier', 
                        'Hier', 
                        'Aujourd\'hui'
                    ];

                    const aaDayIndex = daysOrder.indexOf( aa );
                    const bbDayIndex = daysOrder.indexOf( bb );

                    if( aaDayIndex === -1 && bbDayIndex === -1 ){
                        if( dayjs(aa).isBefore(dayjs(bb)) ){
                            return -1;
                        }
                        return 1;
                    }

                    if ( aaDayIndex === bbDayIndex ){
                        return 0;
                    } else if( aaDayIndex > bbDayIndex ){
                        return 1;
                    }

                    return -1;
                })
            )
    
            setMessages(reeorder);
        }
    }, [fetchMessages])

    const handleSubmitMessage = () => {
        if( modalIsOpen && values.text ){
            setSubmit(true);
        }
    }

    const handleQuizSubmitResponse = ( questionId, questionObj, answerKey, nextStep ) => {
        const answer = answerKey === "yes" ? questionObj.answer_yes : questionObj.answer_no;
        const answerText = answerKey === "yes" ? questionObj.txt_yes : questionObj.txt_no;
        setAnswers(oldVal => ({ 
            ...oldVal, 
            nextFileCode: answer,
            [questionId]: { question: questionObj.question, answer: answerText } 
        }));
        switch( answer ){
            case 'NEXT' :
                setQuizStep(nextStep);
                return ;
            case 'EXIT' :
                setQuizNextFileMessageVisible(true);
                return ;
            default :
                break ;
        }
        if( !answer.includes(':') ) return handleQuizSubmitResponse(questionId, questionObj, 'no', nextStep);

        setQuizNextFileMessageVisible(true);
        setQuizNextFileMessageRequired(false);
    }

    const validateButtonIsDisabled = ( isEmpty(answers?.nextFileComment??null) && quizNextFileMessageRequired );

    const handleBackNextFileMessage = ( ) => {
        setAnswers(oldVal => ({ ...oldVal, nextFileComment: '' }));
        setQuizNextFileMessageVisible(false);
        setQuizNextFileMessageRequired(true);
    }

    const handleValidateNextFileMessage = async ( ) => {
        if( validateButtonIsDisabled ) return ;
        await postNextFile();

        setQuizStep(0);
        setQuizNextFileMessageVisible(false);
        
        doReload(true);
    }

    return (
        <>
            { contextHolder }
            <Modal 
                open={modalIsOpen}
                width={600}
                height={770}
                footer={null}
                closeIcon={null}
                closable={false}
                // mask={false}
                destroyOnClose={true}
                className={styles.modalContent}
            >
                <Row style={{ height: '30px' }}>
                    <div className="ms-auto" style={{ cursor: 'pointer' }} onClick={() => setModalIsOpen(oldVal => !oldVal)}>
                        <CloseModal height={28} width={28} />
                    </div>
                </Row>
                {
                    summary &&
                    <Card 
                        className="mt-2" 
                        style={{ height: '200px', overflow: 'auto' }}
                    >
                        { parse(summary || "") }
                    </Card>
                }
                <Card 
                    ref={messagesBoxRef}
                    className="mt-2" 
                    style={{ maxHeight: '270px', overflow: 'auto' }}
                >
                    {
                        quizNextFileMessageVisible
                        ?
                            <>
                                <Text>Commentaire { quizNextFileMessageRequired === false && '(optionnel)' } :</Text>
                                <CustomTextarea 
                                    name="nextFileComment"
                                    textareaRef={quizNextFileTextareaRef}
                                    textareaHandler={{
                                        values: {
                                            text: answers?.nextFileComment ?? '',
                                            attachments: answers?.attachments ?? []
                                        },
                                        setValues: setAnswers,
                                        initialValues: {}
                                    }}
                                    textareaHeight={quizNextFileTextareaHeight}
                                    pj
                                />  
                                <Row gutter={4} className="mt-2 justify-content-end">
                                    <Col>
                                        <Button type="primary" disabled={validateButtonIsDisabled} style={ !validateButtonIsDisabled ? { backgroundColor: 'green' } : null } onClick={() => handleValidateNextFileMessage()}>Confirmer</Button>
                                    </Col>
                                    <Col>
                                        <Button type="primary" danger onClick={() => handleBackNextFileMessage()}>Précédent</Button>
                                    </Col>
                                </Row>
                            </>
                        :
                        isNonEmpty(fetchQuiz)
                        ?
                            <>
                                {
                                    fetchQuiz.map((el, idx) => {
                                        if( idx !== quizStep ) return null;
                                        return (
                                            <React.Fragment key={idx}>
                                                <div>{ el.question }</div>
                                                <Row gutter={4} className="mt-2 justify-content-end">
                                                    <Col>
                                                        <Button type="primary" onClick={() => handleQuizSubmitResponse('question_'+(idx+1),el,'yes',idx+1)}>{ el.txt_yes }</Button>
                                                    </Col>
                                                    <Col>
                                                        <Button type="primary" danger onClick={() => handleQuizSubmitResponse('question_'+(idx+1),el,'no',idx+1)}>{ el.txt_no }</Button>
                                                    </Col>
                                                </Row>
                                            </React.Fragment>
                                        );
                                    })
                                }
                            </>
                        :
                        isNonEmptyObject(messages)
                        ?
                            <>
                                {
                                    Object.entries(messages)
                                        .map(([key, val]) => {
                                            return (
                                                <React.Fragment key={key}>
                                                    <Row className="justify-content-center">
                                                        <Tag color="#9c9a97">
                                                            { key }
                                                        </Tag>
                                                    </Row>
                                                    {
                                                        val.map((v,i) => {
                                                            const { msg_direction, msg_time, msg_txt, attachments } = v;

                                                            return (
                                                                <Row key={i} ref={rowChatRef}>
                                                                    <Col xs={24} className={['d-flex', msg_direction === 'out' ? 'justify-content-end' : ''].join(" ")}>
                                                                        <ConversationBubble 
                                                                            direction={ i === 0 ? msg_direction : undefined } 
                                                                            bubbleColor={ msg_direction === 'out' ? '#0b6fba' : undefined}
                                                                        >
                                                                            <p style={{ whiteSpace: "pre-line", margin: "0px" }}>{ msg_txt }</p>
                                                                        </ConversationBubble>
                                                                    </Col>
                                                                    <Col 
                                                                        style={{ 
                                                                            width: '280px', 
                                                                            marginLeft: msg_direction === 'out' && rowChatWidth ? (rowChatWidth - 280) + 'px' : undefined
                                                                        }}
                                                                    >
                                                                        {
                                                                            attachments
                                                                            &&
                                                                                attachments.map((attach, idx) => {
                                                                                    const { bin_id, bin_name, bin_type } = attach;
                                                                                    const sliceTo = 32;
                                                                                    let bin_name_sliced;
                                                                                    if( bin_name.length > sliceTo ){
                                                                                        bin_name_sliced = bin_name.slice(0, sliceTo) + '...';
                                                                                    }
                                                                                    return (
                                                                                        <div 
                                                                                            key={idx} 
                                                                                            style={{ cursor: 'pointer' }}
                                                                                            onClick={() => setBinId(bin_id)}
                                                                                        >
                                                                                            {
                                                                                                bin_name_sliced
                                                                                                ?
                                                                                                    <Tooltip title={bin_name} overlayStyle={{ maxWidth: '500px' }}>
                                                                                                        <Button 
                                                                                                            type="text" 
                                                                                                            icon={
                                                                                                                <Image 
                                                                                                                    src={defineSrc(bin_type)} 
                                                                                                                    preview={false} 
                                                                                                                    height={22} 
                                                                                                                />
                                                                                                            }
                                                                                                            styles={{
                                                                                                                icon: {
                                                                                                                    marginRight: 0
                                                                                                                }
                                                                                                            }}
                                                                                                            className="p-0 d-flex align-items-center"
                                                                                                        >
                                                                                                            { bin_name_sliced }
                                                                                                        </Button>
                                                                                                    </Tooltip>
                                                                                                :
                                                                                                    <Button 
                                                                                                        type="text" 
                                                                                                        icon={
                                                                                                            <Image 
                                                                                                                src={defineSrc(bin_type)} 
                                                                                                                preview={false} 
                                                                                                                height={22} 
                                                                                                            />
                                                                                                        }
                                                                                                        styles={{
                                                                                                            icon: {
                                                                                                                marginRight: 0
                                                                                                            }
                                                                                                        }}
                                                                                                        className="p-0 d-flex align-items-center"
                                                                                                    >
                                                                                                        { bin_name }
                                                                                                    </Button>
                                                                                            }
                                                                                        </div>
                                                                                    );
                                                                                })
                                                                        }
                                                                    </Col>
                                                                    <Col 
                                                                        style={{ 
                                                                            width: '280px', 
                                                                            marginLeft: msg_direction === 'out' && rowChatWidth ? (rowChatWidth - 280) + 'px' : undefined
                                                                        }}
                                                                    >
                                                                        <span className="d-flex justify-content-end">{ msg_time }</span>
                                                                    </Col>
                                                                </Row>
                                                            );
                                                        })
                                                    }
                                                </React.Fragment>
                                            );
                                    })
                                }
                            </>
                        :
                            <Empty />
                    }
                </Card>
                {
                    isEmpty(fetchQuiz) &&
                        <Card 
                            bordered={false} 
                            style={{ 
                                boxShadow: 'none', 
                                height: '200px', 
                                maxHeight: '235px'
                            }} 
                            bodyStyle={{ 
                                padding: 0, 
                                height: '100%' 
                            }}
                        >
                            <CustomTextarea 
                                textareaRef={textareaRef}
                                textareaHandler={textareaValuesHandler}
                                textareaHeight={textareaHeight}
                                pj={true}
                                sendButton={true}
                                onSubmitMessage={handleSubmitMessage}
                            />    
                        </Card>
                }
            </Modal>
        </>
    );
}